import { Title as T, Body as Bd } from "sunday-component";
import { Modal as M, Image, Skeleton } from "antd";
import { down } from "styled-breakpoints";
import styled from "styled-components";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { checkUnknownError } from "src/utils/errors";
import { logError } from "src/utils/logger";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { color } from "../../styles/globals";
import { getConsentsByCode } from "../../api/consent";
import Button from "../Common/Button";

const Modal = styled(M)<{ footer?: JSX.Element }>`
  max-width: 100vw;
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  .ant-modal-header {
  }
  .ant-modal-close {
    margin-top: 10px;
  }
  .ant-modal-body {
    padding: 20px 16px;
    height: 70vh;
    background-color: ${color.charcoal01};
    overflow: auto;
  }
  .ant-modal-footer {
    display: ${(props) => (props.footer ? "block" : "none")};
    width: 100%;
    background-color: ${color.white};
  }
  ${down("lg")} {
    top: 0;
    margin: 0px;
    padding: 0px;
    width: 100% !important;
    .ant-modal,
    .ant-modal-content {
      border-radius: 0px !important;
      height: 100vh;
      width: 100vw;
      margin: 0;
      top: 0;
    }
    .ant-modal-body {
      overflow: auto;
      height: calc(100vh - 141px);
    }
    .ant-modal-footer {
      padding: 16px;
      position: fixed;
      bottom: 0;
      width: 100%;
      background: white;
    }
  }
`;

const CloseIcon = styled(Image)`
  cursor: pointer;
`;

const ModalBody = styled(Bd)`
  color: ${color.charcoal09};
`;

const Title = styled(T)`
  color: ${color.charcoal10};
`;

export type ConsentModalProp = {
  title: string;
  code: string;
  visible: boolean;
  onCloseModal?: Function;
  lang: string;
  footer?: JSX.Element;
};

const ConsentModal = ({
  title = "",
  code = "",
  visible = false,
  onCloseModal = () => {},
  lang = "en",
  footer = null,
}: ConsentModalProp) => {
  const { t } = useTranslation("application-page");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const isMounted = useRef(false);
  const { lg } = useBreakpoint();

  const onDisplayConsent = async () => {
    setIsLoading(true);
    setIsLoadingError(false);
    try {
      setDescription("");
      const consent = await getConsentsByCode(code, lang);
      if (!consent) {
        setIsLoadingError(true);
      }
      setIsLoadingError(false);
      setDescription(consent.detail);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsLoadingError(true);
      const isUnknowError = checkUnknownError(error);
      if (isUnknowError) {
        logError(
          "[consent-modal-component] display consent fail",
          { code, lang },
          error
        );
      }
    }
  };

  useEffect(() => {
    if (isMounted.current && visible) {
      onDisplayConsent();
    }
    isMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const styles = {
    modalTitle: {
      minHeight: "24px",
      margin: "0px 56px",
    },
    noDataLogo: {
      height: "144px",
      marginBottom: "17px",
    },
    errorBox: {
      width: "255px",
      margin: "28px auto",
    },
    errorMessage: {
      marginBottom: "17px",
      margin: "0px auto 17px auto",
    },
    errorMessageTitle: {
      marginButton: "8px",
    },
    refreshButton: {
      width: "172px",
    },
  };

  return (
    <Modal
      centered={lg}
      title={
        <Title level={6} style={{ textAlign: "center", ...styles.modalTitle }}>
          {title}
        </Title>
      }
      footer={footer}
      visible={visible}
      closeIcon={
        <CloseIcon
          preview={false}
          src="/static/pages/planPage/x-icon.svg"
          alt="close-icon"
          onClick={() => onCloseModal()}
        />
      }
    >
      <Skeleton
        loading={isLoading}
        active
        title={false}
        paragraph={{ rows: 15, width: "100%" }}
      >
        <ModalBody level={7}>{description}</ModalBody>
      </Skeleton>
      {isLoadingError && (
        <div style={{ textAlign: "center", ...styles.errorBox }}>
          <Image
            src="/static/pages/applicationPage/consent-no-data-icon.svg"
            preview={false}
            style={styles.noDataLogo}
            alt="consent-no-data-icon"
          />
          <div style={{ textAlign: "center", ...styles.errorMessage }}>
            <Title level={5}>
              {t("agreementForm.errorMessage.loadingError.title")}
            </Title>
            <ModalBody level={7}>
              {t("agreementForm.errorMessage.loadingError.description")}
            </ModalBody>
          </div>
          <Button
            type="outlined"
            onClick={() => onDisplayConsent()}
            style={styles.refreshButton}
          >
            {t("agreementForm.errorMessage.loadingError.refreshButton")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ConsentModal;
